import { useQuery } from "@tanstack/react-query";
import { isBankAccount, isCashAccount } from "../constants/accountTypes";
import { isBankAccountFormChatEnabled } from "../context/formChatContexts";
import { bankRepository } from "../repositories/bankRepository";
import { bankAccountQueryKey } from "./useBankAccountQueries";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";

/** Whether the bank account chat replaces the classic form, for bank and cash accounts alike. */
export const useShouldUseBankAccountChat = ({
  accountId,
  enabled = true,
}: { accountId?: string; enabled?: boolean } = {}) => {
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isEligible =
    enabled && isBankAccountFormChatEnabled() && Boolean(terreiroId || mediumId);
  const accountQuery = useQuery({
    queryKey: bankAccountQueryKey(accountId),
    queryFn: () => bankRepository.getBankAccount(accountId as string),
    enabled: isEligible && Boolean(accountId),
    staleTime: 1000 * 60 * 5,
  });

  if (!isEligible) return { shouldUseChat: false, isResolving: false };
  if (!accountId) return { shouldUseChat: true, isResolving: false };
  const account = accountQuery.data;
  return {
    shouldUseChat: Boolean(
      account &&
        (isBankAccount(account.accountType) || isCashAccount(account.accountType)),
    ),
    isResolving: accountQuery.isLoading,
  };
};
